import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Loader2 } from 'lucide-react';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  title,
}) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);

  const sizeMap = {
    sm: { padding: '6px 12px', fontSize: '12px', gap: '5px', iconSize: 14, borderRadius: '7px' },
    md: { padding: '9px 18px', fontSize: '14px', gap: '7px', iconSize: 16, borderRadius: '9px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '8px', iconSize: 18, borderRadius: '10px' },
  };

  const sizes = sizeMap[size] || sizeMap.md;

  const variantMap = {
    primary: {
      backgroundColor: isHovered && !disabled && !loading ? theme.colors.primaryHover : theme.colors.primary,
      color: '#ffffff',
      border: 'none',
      boxShadow: isHovered && !disabled && !loading ? `0 4px 12px ${theme.colors.primary}40` : 'none',
    },
    secondary: {
      backgroundColor: isHovered && !disabled && !loading ? theme.colors.backgroundTertiary : theme.colors.backgroundSecondary,
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`,
    },
    outline: {
      backgroundColor: isHovered && !disabled && !loading ? `${theme.colors.primary}08` : 'transparent',
      color: theme.colors.primary,
      border: `1.5px solid ${theme.colors.primary}`,
    },
    ghost: {
      backgroundColor: isHovered && !disabled && !loading ? theme.colors.backgroundSecondary : 'transparent',
      color: isHovered && !disabled && !loading ? theme.colors.text : theme.colors.textSecondary,
      border: `1px solid ${isHovered && !disabled && !loading ? theme.colors.border : 'transparent'}`,
    },
    success: {
      backgroundColor: isHovered && !disabled && !loading ? '#059669' : theme.colors.success,
      color: '#ffffff',
      border: 'none',
      boxShadow: isHovered && !disabled && !loading ? `0 4px 12px ${theme.colors.success}40` : 'none',
    },
    danger: {
      backgroundColor: isHovered && !disabled && !loading ? '#dc2626' : theme.colors.error,
      color: '#ffffff',
      border: 'none',
    },
    warning: {
      backgroundColor: isHovered && !disabled && !loading ? '#d97706' : theme.colors.warning,
      color: '#ffffff',
      border: 'none',
    },
  };

  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: sizes.gap,
    padding: sizes.padding,
    fontSize: sizes.fontSize,
    fontWeight: '600',
    borderRadius: sizes.borderRadius,
    transition: 'all 0.15s ease',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.55 : 1,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    outline: 'none',
    transform: isHovered && !disabled && !loading ? 'translateY(-1px)' : 'translateY(0)',
    lineHeight: 1,
    letterSpacing: '0.01em',
    ...(variantMap[variant] || variantMap.primary),
  };

  const renderIcon = () => {
    if (loading) return <Loader2 size={sizes.iconSize} style={{ animation: 'spin 0.8s linear infinite' }} />;
    if (Icon) return <Icon size={sizes.iconSize} />;
    return null;
  };

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <button
        type={type}
        style={buttonStyle}
        onClick={onClick}
        disabled={disabled || loading}
        className={className}
        title={title}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {iconPosition === 'left' && renderIcon()}
        {children}
        {iconPosition === 'right' && renderIcon()}
      </button>
    </>
  );
};

export default Button;
